import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRightCircle, Loader2, AlertCircle, Trash2, Plus } from 'lucide-react';
import { apiFetch } from '../../utils/api';

const LeadsManagement: React.FC = () => {
    const [leads, setLeads] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [showConvertModal, setShowConvertModal] = useState<string | null>(null);
    const [showAddLeadModal, setShowAddLeadModal] = useState(false);

    // Convert Form Data
    const [convertData, setConvertData] = useState({
        account_name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        opportunity_name: ''
    });

    // New Lead Form Data
    const [newLead, setNewLead] = useState({
        name: '', email: '', phone: '', company_name: '', service: '', budget: '', message: ''
    });


    const fetchLeads = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch('/leads');
            if (res.ok) {
                setLeads(await res.json());
            }
        } catch (err) {
            // Error handled by apiFetch or silently ignored to match original logic
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    // Prevent background scrolling when modal is open
    useEffect(() => {
        if (showConvertModal || showAddLeadModal) {
            document.body.style.overflow = 'hidden';
            const adminMain = document.querySelector('section.custom-scrollbar');
            if (adminMain) (adminMain as HTMLElement).style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            const adminMain = document.querySelector('section.custom-scrollbar');
            if (adminMain) (adminMain as HTMLElement).style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
            const adminMain = document.querySelector('section.custom-scrollbar');
            if (adminMain) (adminMain as HTMLElement).style.overflow = '';
        };
    }, [showConvertModal, showAddLeadModal]);

    const handleConvert = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/crm/leads/convert', {
                method: 'POST',
                body: JSON.stringify({
                    lead_id: showConvertModal,
                    ...convertData
                })
            });
            if (res.ok) {
                setShowConvertModal(null);
                fetchLeads();
                alert('Lead converted to CRM Opportunity successfully!');
            } else {
                const data = await res.json();
                alert(data.message || 'Failed to convert lead');
            }
        } catch (err) {
            alert('Error converting lead');
        }
    };

    const handleAddLead = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await apiFetch('/leads', {
                method: 'POST',
                body: JSON.stringify(newLead)
            });
            if (res.ok) {
                setShowAddLeadModal(false);
                setNewLead({ name: '', email: '', phone: '', company_name: '', service: '', budget: '', message: '' });
                fetchLeads();
                alert('Lead added successfully!');
            } else {
                alert('Failed to add lead');
            }
        } catch (err) {
            alert('Error adding lead');
        }
    };

    return (
        <div className="space-y-8">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Leads Management</h2>
                <button
                    onClick={() => setShowAddLeadModal(true)}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
                >
                    <Plus size={18} /> Add Lead
                </button>
            </div>

            {loading ? <Loader2 className="animate-spin h-8 w-8 text-blue-600 mx-auto" /> : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50/50 text-xs font-bold text-gray-400 uppercase tracking-widest">
                            <tr>
                                <th className="px-6 py-4">Name</th>
                                <th className="px-6 py-4">Contact</th>
                                <th className="px-6 py-4">Service</th>
                                <th className="px-6 py-4">Status</th>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {leads.map(lead => (
                                <tr key={lead.id} className="hover:bg-gray-50/50">
                                    <td className="px-6 py-4 font-medium text-gray-900">{lead.name}</td>
                                    <td className="px-6 py-4 text-sm text-gray-500">
                                        <div>{lead.email}</div>
                                        <div className="text-xs">{lead.phone}</div>
                                    </td>
                                    <td className="px-6 py-4">{lead.service}</td>
                                    <td className="px-6 py-4">
                                        <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${lead.status === 'New' ? 'bg-blue-100 text-blue-700' :
                                            lead.status === 'Converted' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                            }`}>
                                            {lead.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-500">{new Date(lead.created_at).toLocaleDateString()}</td>
                                    <td className="px-6 py-4 flex items-center gap-3">
                                        {lead.status !== 'Converted' && (
                                            <button
                                                onClick={() => {
                                                    setShowConvertModal(lead.id);
                                                    setConvertData({
                                                        account_name: lead.company_name || lead.name,
                                                        contact_name: lead.name,
                                                        contact_email: lead.email || '',
                                                        contact_phone: lead.phone || '',
                                                        opportunity_name: `${lead.service || 'Project'} Opportunity`
                                                    });
                                                }}
                                                className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-bold"
                                                title="Convert to Opportunity"
                                            >
                                                <ArrowRightCircle size={16} />
                                            </button>
                                        )}
                                        <button
                                            onClick={async () => {
                                                if (window.confirm('Are you sure you want to delete this lead? This action cannot be undone.')) {
                                                    try {
                                                        const res = await apiFetch(`/leads/${lead.id}`, {
                                                            method: 'DELETE'
                                                        });
                                                        if (res.ok) {
                                                            fetchLeads();
                                                            alert('Lead deleted successfully');
                                                        } else {
                                                            alert('Failed to delete lead');
                                                        }
                                                    } catch (err) {
                                                        console.error(err);
                                                        alert('Error deleting lead');
                                                    }
                                                }
                                            }}
                                            className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm font-bold"
                                            title="Delete Lead"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showConvertModal && (
                <div 
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto overscroll-contain no-scrollbar"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowConvertModal(null);
                    }}
                >
                    <div 
                        className="bg-white rounded-3xl p-8 max-w-2xl w-full shadow-2xl animate-in fade-in zoom-in duration-200 my-auto max-h-[90vh] overflow-y-auto overscroll-contain no-scrollbar"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-800">Convert Lead to Opportunity</h3>
                            <button onClick={() => setShowConvertModal(null)} className="text-gray-400 hover:text-gray-600 font-bold p-2 transition-colors text-2xl leading-none">×</button>
                        </div>

                        <form onSubmit={handleConvert} className="space-y-6">
                            <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100 flex items-center gap-4 text-sm text-blue-700">
                                <AlertCircle size={20} className="shrink-0" />
                                <p>This will create a new <strong>Account</strong>, <strong>Contact</strong>, and <strong>Opportunity</strong> in your CRM.</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1">
                                    <label className="text-sm font-bold text-gray-700">Account (Company) Name *</label>
                                    <input className="w-full border-gray-200 border p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none" value={convertData.account_name} onChange={e => setConvertData({ ...convertData, account_name: e.target.value })} required />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-bold text-gray-700">Opportunity Name *</label>
                                    <input className="w-full border-gray-200 border p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none" value={convertData.opportunity_name} onChange={e => setConvertData({ ...convertData, opportunity_name: e.target.value })} required />
                                </div>
                            </div>

                            <div className="border-t border-gray-100 pt-6">
                                <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-4">Primary Contact Details</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-1">
                                        <label className="text-sm font-bold text-gray-700">Contact Name *</label>
                                        <input className="w-full border-gray-200 border p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none" value={convertData.contact_name} onChange={e => setConvertData({ ...convertData, contact_name: e.target.value })} required />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-bold text-gray-700">Email Address</label>
                                        <input type="email" className="w-full border-gray-200 border p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none" value={convertData.contact_email} onChange={e => setConvertData({ ...convertData, contact_email: e.target.value })} />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-bold text-gray-700">Phone Number</label>
                                        <input className="w-full border-gray-200 border p-3 rounded-xl focus:ring-2 focus:ring-blue-500/20 outline-none" value={convertData.contact_phone} onChange={e => setConvertData({ ...convertData, contact_phone: e.target.value })} />
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-4 pt-6">
                                <button type="button" onClick={() => setShowConvertModal(null)} className="flex-1 py-3 text-gray-500 font-bold hover:bg-gray-100 rounded-xl transition-colors border border-gray-100">Cancel</button>
                                <button type="submit" className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25">Convert to CRM</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showAddLeadModal && (
                <div 
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto overscroll-contain no-scrollbar"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowAddLeadModal(false);
                    }}
                >
                    <div 
                        className="bg-white rounded-3xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto overscroll-contain my-auto shadow-2xl animate-in fade-in zoom-in duration-200 no-scrollbar"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="text-2xl font-bold text-gray-800 mb-6">Add New Lead</h3>
                        <form onSubmit={handleAddLead} className="space-y-4">
                            <div className="space-y-1">
                                <label className="text-sm font-semibold text-gray-700">Name *</label>
                                <input className="w-full border p-2 rounded" placeholder="John Doe" value={newLead.name} onChange={e => setNewLead({ ...newLead, name: e.target.value })} required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Email</label>
                                    <input className="w-full border p-2 rounded" placeholder="john@example.com" value={newLead.email} onChange={e => setNewLead({ ...newLead, email: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-sm font-semibold text-gray-700">Phone</label>
                                    <input className="w-full border p-2 rounded" placeholder="+1234567890" value={newLead.phone} onChange={e => setNewLead({ ...newLead, phone: e.target.value })} />
                                </div>
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-semibold text-gray-700">Company Name</label>
                                <input className="w-full border p-2 rounded" placeholder="Acme Inc" value={newLead.company_name} onChange={e => setNewLead({ ...newLead, company_name: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-semibold text-gray-700">Service *</label>
                                <select
                                    className="w-full border p-2 rounded"
                                    value={newLead.service}
                                    onChange={e => setNewLead({ ...newLead, service: e.target.value })}
                                    required
                                >
                                    <option value="">Select a service</option>
                                    <option value="Web Development">Web Development</option>
                                    <option value="Mobile App Development">Mobile App Development</option>
                                    <option value="Azure/AWS Cloud Solutions">Azure/AWS Cloud Solutions</option>
                                    <option value="M365 Development">M365 Development</option>
                                    <option value="Database Design">Database Design</option>
                                    <option value="DevOps Services">DevOps Services</option>
                                    <option value="Technical Consulting">Technical Consulting</option>
                                    <option value="Digital Marketing">Digital Marketing</option>
                                    <option value="Corporate Training">Corporate Training</option>
                                    <option value="Sub Contracting">Sub Contracting</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-semibold text-gray-700">Budget</label>
                                <input className="w-full border p-2 rounded" placeholder="$5000" value={newLead.budget} onChange={e => setNewLead({ ...newLead, budget: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <label className="text-sm font-semibold text-gray-700">Message / Description</label>
                                <textarea className="w-full border p-2 rounded" rows={3} placeholder="Project requirements..." value={newLead.message} onChange={e => setNewLead({ ...newLead, message: e.target.value })} />
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button type="button" onClick={() => setShowAddLeadModal(false)} className="flex-1 py-2 text-gray-600 font-bold border rounded hover:bg-gray-50">Cancel</button>
                                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white font-bold rounded hover:bg-blue-700">Create Lead</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LeadsManagement;
